/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable no-multi-assign */
// Material UI: Theming
import {
  alpha,
  Box,
  createTheme,
  CssBaseline,
  lighten,
  Paper,
  ThemeProvider as MuiV4ThemeProvider,
  withStyles,
} from '@material-ui/core';
import { common, grey } from '@material-ui/core/colors';
import MuiShadows from '@material-ui/core/styles/shadows';
import transitions from '@material-ui/core/styles/transitions';
import { ArrowDropDownRounded } from '@material-ui/icons';
import CloseRoundedIcon from '@material-ui/icons/CloseRounded';
import { appConfig } from '@src/config/appConfig';
import { useThemeMode } from '@src/contexts/ThemeMode';
import { useGetCustomUiConfigQuery } from '@src/reducers/partnerCustomUiApi';
import { isLoggedInSelect } from '@src/selectors/user';
import { MuiTheme } from 'material-ui/styles';
import baseMuiTheme from 'material-ui/styles/baseThemes/lightBaseTheme';
import * as muiColors from 'material-ui/styles/colors';
import getMuiTheme from 'material-ui/styles/getMuiTheme';
import { fade } from 'material-ui/utils/colorManipulator';
import { useSelector } from 'react-redux';
import {
  DARK_BG_CARD,
  DARK_BG_DEFAULT,
  DARK_BG_INPUT,
  DARK_BORDER,
  DARK_TEXT_DISABLED,
  DARK_TEXT_PRIMARY,
  DARK_TEXT_SECONDARY,
} from '@src/muithemes/darkModeColors';

declare module '@material-ui/core/styles/createTheme' {
  interface Theme {
    chatItem: {
      color: string;
      activeBgColor: string;
      hover: string;
    };
  }

  interface ThemeOptions {
    chatItem: {
      color: string;
      activeBgColor: string;
      hover: string;
    };
  }
}

declare module '@material-ui/core/styles/createTheme' {
  interface Theme {
    sideMenuTab: {
      background: string;
      backgroundSelected: string;
      shadow: string;
      shadowSelected: string;
      iconFill: string;
      imgFilter: string;
    };
  }
  interface ThemeOptions {
    sideMenuTab: {
      background: string;
      backgroundSelected: string;
      shadow: string;
      shadowSelected: string;
      iconFill: string;
      imgFilter: string;
    };
  }
}

declare module '@material-ui/core/styles/createTheme' {
  interface Theme {
    popoverSurface: {
      background: string;
      border: string;
      shadow: string;
    };
  }
  interface ThemeOptions {
    popoverSurface: {
      background: string;
      border: string;
      shadow: string;
    };
  }
}

declare module '@material-ui/core/styles/createTheme' {
  interface Theme {
    mfaEnrollment: {
      pageBg: string;
      instructionsBg: string;
      secretBg: string;
      ackBg: string;
    };
  }
  interface ThemeOptions {
    mfaEnrollment: {
      pageBg: string;
      instructionsBg: string;
      secretBg: string;
      ackBg: string;
    };
  }
}

// Knowledge Base file rows and the read-only document viewer (AI-agent config).
interface KnowledgeFileTokens {
  rowBg: string;
  actionHoverBg: string;
  deleteHoverBg: string;
  disabledIcon: string;
  iconCircleBg: string;
  viewerBodyBg: string;
  pageBg: string;
  pageShadow: string;
  pageBorder: string;
  highlightBg: string;
  highlightCurrentBg: string;
  highlightCurrentText: string;
  /** Viewer body scrollbar: the global thumb (#e4e4e7 on transparent) vanishes on the viewer's grey. */
  scrollThumb: string;
  scrollTrack: string;
  /** Amber text of the "not available to view or download" row notice (ENG-2054). */
  legacyNotice: string;
  /** Grey of the fixed note under the list (design `sec`). */
  noteText: string;
}

declare module '@material-ui/core/styles/createTheme' {
  interface Theme {
    knowledgeFile: KnowledgeFileTokens;
  }
  interface ThemeOptions {
    knowledgeFile: KnowledgeFileTokens;
  }
}

declare module '@material-ui/core/styles/createTheme' {
  interface Theme {
    serviceMessagesSummary: {
      billableBg: string;
      billableBorder: string;
      billableDivider: string;
    };
  }
  interface ThemeOptions {
    serviceMessagesSummary: {
      billableBg: string;
      billableBorder: string;
      billableDivider: string;
    };
  }
}

declare module '@material-ui/core/styles/createTheme' {
  interface Theme {
    timelineEvent: {
      phoneLinkedIconBg: string;
      phoneLinkedAccent: string;
    };
  }
  interface ThemeOptions {
    timelineEvent: {
      phoneLinkedIconBg: string;
      phoneLinkedAccent: string;
    };
  }
}

declare module '@material-ui/core/styles/createTheme' {
  interface Theme {
    b2chatblue: {
      '50': string;
      '100': string;
      '200': string;
    };
    b2chatgray: {
      '50': string;
      '100': string;
      '200': string;
      '300': string;
      '400': string;
      '500': string;
      '600': string;
      '700': string;
      '800': string;
      '900': string;
    };
    b2chatyellow: {
      '300': string;
      '500': string;
    };
    b2chatgreen: {
      '50': string;
      '300': string;
      '500': string;
      '600': string;
    };
    b2chatred: {
      '50': string;
      '100': string;
      '500': string;
    };
    b2chatorange: {
      '100': string;
      '500': string;
      '600': string;
    };
    capicolor: {
      blue: string;
      orange: string;
      green: string;
      purple: string;
      purpleBg: string;
    };
  }
  interface ThemeOptions {
    b2chatblue: {
      '50': string;
      '100': string;
      '200': string;
    };
    b2chatgray: {
      '50': string;
      '100': string;
      '200': string;
      '400': string;
      '500': string;
      '600': string;
      '700': string;
      '800': string;
      '900': string;
    };
    b2chatyellow: {
      '300': string;
      '500': string;
    };
    b2chatgreen: {
      '50': string;
      '500': string;
      '600': string;
    };
    b2chatred: {
      '50': string;
      '100': string;
      '500': string;
    };
    b2chatorange: {
      '100': string;
      '500': string;
      '600': string;
    };
    capicolor: {
      blue: string;
      orange: string;
      green: string;
      purple: string;
      purpleBg: string;
    };
  }
}

// Define an object with customized values for the MUI theme fields. Use the source code to see the values that
// can be overriden: https://github.com/callemall/material-ui/blob/master/src/styles/getMuiTheme.js,
// ./node_modules/material-ui/styles/baseThemes/lightBaseTheme.js
const customMuiThemeValues = {
  fontFamily: 'Poppins, sans-serif',
  fontSize: 14,
  appBar: {
    color: muiColors.black,
  },
  raisedButton: {
    color: '#2196F3',
    textColor: muiColors.white,
    disabledColor: fade(muiColors.grey500, 0.2),
    disabledTextColor: fade(muiColors.grey600, 0.2),
  },
  flatButton: {
    color: muiColors.transparent,
    buttonFilterColor: '#999999',
    disabledTextColor: fade(muiColors.grey600, 0.2),
    textColor: muiColors.blue500,
    primaryTextColor: muiColors.blue500,
  },
  badge: {
    primaryColor: muiColors.red500,
    primaryTextColor: muiColors.white,
    secondaryColor: muiColors.red500,
    secondaryTextColor: muiColors.white,
  },
  textField: {
    focusColor: muiColors.blue500,
  },
  palette: {
    primary1Color: muiColors.white,
    primary2Color: muiColors.grey600,
    alternateTextColor: muiColors.black,
    accent1Color: muiColors.blue500,
  },
  stepper: {
    backgroundColor: 'transparent',
    hoverBackgroundColor: fade(muiColors.black, 0.06),
    iconColor: muiColors.blue500,
    hoveredIconColor: muiColors.grey700,
    inactiveIconColor: muiColors.grey500,
    textColor: fade(muiColors.black, 0.87),
    disabledTextColor: fade(muiColors.black, 0.26),
    connectorLineColor: muiColors.grey400,
  },
  checkbox: {
    boxColor: muiColors.grey600,
    checkedColor: '#b7b7cf',
    labelColor: '#b7b7cf',
  },
  radioButton: {
    checkedColor: muiColors.grey600,
    borderColor: muiColors.grey600,
    labelColor: muiColors.grey600,
  },
  toggle: {
    trackOnColor: '#2196F3',
  },
};

// Build a custom MUI theme from an existing base theme.
// Base theme values are overriden by providing a second argument. The resulting custom theme will be
// deeply merged with the second argument.
const customMuiTheme = getMuiTheme(
  baseMuiTheme,
  customMuiThemeValues as unknown as MuiTheme
);

export const primary = {
  main: '#7C69FF',
  contrastText: '#FFFFFF',
  light: '#6f5bff',
};

const text = {
  primary: '#323444',
  disabled: '#A1A1AA',
};

const b2chatblue = {
  '50': '#7C69FF',
  '100': '#E9EAED',
  '200': '#ECE9FF',
} as const;

const b2chatgray = {
  '50': '#FAFAFA',
  '100': '#F4F4F5',
  '200': '#E4E4E7',
  '300': '#D4D4D8',
  '400': '#A1A1AA',
  '500': '#71717A',
  '600': '#52525B',
  '700': '#3F3F46',
  '800': '#27272A',
  '900': '#18181B',
} as const;

const b2chatyellow = {
  '300': '#FEF2C4',
  '500': '#E0B300',
};

const b2chatgreen = {
  '50': '#E1F2DE',
  '300': '#86CB7B',
  '500': '#25C16F',
  '600': '#27E381',
};

export const b2chatred = {
  '50': '#FFF0EE',
  '100': '#FA4C56',
  '500': '#F27468',
};

const b2chatorange = {
  '100': '#FDE5C6',
  '500': '#D97D54',
  '600': '#F8981D',
};

export const capicolor = {
  blue: '#1a73e8',
  orange: '#F8981D',
  green: '#86CB7B',
  purple: '#5b21b6',
  purpleBg: '#ede9fe',
};
const chatitem = {
  color: '#fafafa',
  activeBgColor: '#efedff',
  hover: '#f8f8f8',
};

type Shadows = typeof MuiShadows &
  Record<'xxs' | 'xs' | 's' | 'm' | 'l', string>;

const shadows = [...MuiShadows] as Shadows;
shadows.xxs = shadows[1] = '0px 4px 4px rgba(0, 0, 0, 0.05)';
shadows.xs = shadows[2] = '0px 8px 8px 0px #00000014';
shadows.s = shadows[3] = '0px 8px 16px rgba(0, 0, 0, 0.16)';
shadows.m = shadows[4] = '0px 16px 24px rgba(0, 0, 0, 0.24)';
shadows.l = shadows[5] = '0px 16px 32px rgba(0, 0, 0, 0.32)';

export const PaperRounded = withStyles({
  root: {
    border: `1px solid ${grey[300]}`,
    borderRadius: 8,
  },
})(Paper);

export const CustomMuiV4ThemeProvider: React.FC<
  React.PropsWithChildren<unknown>
> = ({ children }) => {
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';
  const loggedIn = useSelector(isLoggedInSelect);

  const query = useGetCustomUiConfigQuery(undefined, {
    skip: !loggedIn,
  });

  const partnersCustomUi = query.data || appConfig.getData('partnerCustomUi');

  const primaryColorConfig = {
    ...primary,
    light: partnersCustomUi?.primaryColor
      ? lighten(partnersCustomUi?.primaryColor, 0.15)
      : primary.light,
    main: partnersCustomUi?.primaryColor || primary.main,
  };

  const chatItemColorConfig = {
    ...chatitem,
    // TODO: This value should be retrieved from backend custom-ui settings
    // eslint-disable-next-line no-nested-ternary
    activeBgColor: isDark
      ? '#272B33'
      : partnersCustomUi?.primaryColor
      ? '#f9ffed'
      : chatitem.activeBgColor,
    hover: isDark ? 'rgba(255,255,255,0.06)' : chatitem.hover,
  };

  // Single source of truth for the agent-console side-menu tab surfaces.
  // Branch on isDark once here so consumers (TabButton) read tokens, not ternaries.
  const sideMenuTabConfig = {
    background: isDark ? b2chatgray[800] : b2chatgray[100],
    backgroundSelected: isDark ? DARK_BG_CARD : common.white,
    shadow: isDark
      ? '0px 2px 6px 0px rgba(0,0,0,0.4)'
      : '0px 2px 6px 0px rgba(0,0,0,0.08)',
    shadowSelected: isDark
      ? '0px 2px 8px 0px rgba(0,0,0,0.5)'
      : '0px 2px 8px 0px rgba(0,0,0,0.12)',
    iconFill: isDark ? 'rgba(255,255,255,0.45)' : b2chatgray[500],
    imgFilter: isDark
      ? 'grayscale(100%) brightness(160%) contrast(60%)'
      : 'grayscale(100%) brightness(180%) contrast(70%)',
  };

  // Single source of truth for floating surfaces (popovers, menus, the avatar
  // status popper). In dark mode a black drop-shadow is invisible and the
  // default paper matches the cards behind it, so lift the surface onto a
  // lighter shade with a hairline border for clear separation.
  const popoverSurfaceConfig = {
    background: isDark ? DARK_BG_INPUT : common.white,
    border: isDark ? `1px solid ${DARK_BORDER}` : 'none',
    shadow: isDark
      ? '0px 12px 32px 0px rgba(0,0,0,0.6)'
      : '0px 16px 24px 0px #0000003D',
  };

  // Single source of truth for MFA enrollment screen surfaces.
  // Branch on isDark once here so components read tokens, not ternaries.
  const mfaEnrollmentConfig = {
    pageBg: isDark ? DARK_BG_DEFAULT : '#f5f3f8',
    instructionsBg: isDark ? DARK_BG_DEFAULT : grey[50],
    secretBg: isDark ? DARK_BG_DEFAULT : grey[100],
    ackBg: isDark ? DARK_BG_DEFAULT : '#FFF8E1',
  };

  // Single source of truth for the Knowledge Base rows and the document
  // viewer. Branch on isDark once here so components read tokens, not ternaries.
  const knowledgeFileConfig = {
    rowBg: isDark ? DARK_BG_INPUT : b2chatgray[50],
    actionHoverBg: fade(primaryColorConfig.main, isDark ? 0.22 : 0.1),
    deleteHoverBg: fade(b2chatred[500], isDark ? 0.18 : 0.08),
    disabledIcon: isDark ? DARK_TEXT_DISABLED : b2chatgray[300],
    iconCircleBg: fade(primaryColorConfig.main, isDark ? 0.22 : 0.1),
    viewerBodyBg: isDark ? DARK_BG_DEFAULT : '#f4f3f6',
    pageBg: isDark ? DARK_BG_CARD : common.white,
    pageShadow: isDark ? 'none' : '0px 1px 2px rgba(0, 0, 0, 0.05)',
    pageBorder: isDark ? `1px solid ${DARK_BORDER}` : 'none',
    highlightBg: isDark ? 'rgba(224, 179, 0, 0.35)' : b2chatyellow[300],
    highlightCurrentBg: b2chatyellow[500],
    highlightCurrentText: isDark ? DARK_BG_DEFAULT : '#323444',
    // The track stays transparent: with a reserved gutter it would paint a
    // band even when there is nothing to scroll. The thumb carries the contrast.
    scrollThumb: isDark ? 'rgba(255, 255, 255, 0.3)' : b2chatgray[400],
    scrollTrack: 'transparent',
    // Values of the I002 design export (`warn`): >= 4.5:1 on the light row
    // (#FAFAFA) and on the dark one (#252932).
    legacyNotice: isDark ? '#FEC84B' : '#B54708',
    noteText: isDark ? DARK_TEXT_SECONDARY : b2chatgray[500],
  };

  // Single source of truth for the service-messages summary "Facturados" card.
  // Branch on isDark once here so the component reads tokens, not ternaries.
  const serviceMessagesSummaryConfig = {
    billableBg: isDark ? 'rgba(124,105,255,0.25)' : '#ECE9FF',
    billableBorder: isDark ? 'rgba(124,105,255,0.4)' : '#ECE9FF',
    billableDivider: isDark ? 'rgba(124,105,255,0.4)' : '#DAD4FF',
  };

  // Inline timeline system events: the phone-linked icon circle and the linked number
  const timelineEventConfig = {
    phoneLinkedIconBg: isDark ? 'rgba(74, 222, 128, 0.16)' : b2chatgreen[50],
    phoneLinkedAccent: isDark ? '#4ADE80' : '#16A34A',
  };

  // Expose the resolved primary color as a CSS custom property so plain CSS
  // files (e.g. react-day-picker overrides) can reference it without hardcoding.
  document.documentElement.style.setProperty(
    '--b2chat-primary',
    primaryColorConfig.main
  );

  const theme = createTheme({
    typography: {
      htmlFontSize: 14,
      fontFamily: 'Poppins, sans-serif',
      fontWeightLight: 300,
      fontWeightRegular: 400,
      fontWeightMedium: 500,
      fontWeightBold: 600,
      h1: {
        fontSize: '2.75rem',
        fontWeight: 500,
        lineHeight: 1.28,
      },
      h2: {
        fontSize: '1.85rem',
        fontWeight: 500,
        lineHeight: 1.2,
      },
      h3: {
        fontSize: '1.375rem',
        fontWeight: 500,
        lineHeight: 1.24,
      },
      h4: {
        fontSize: '1.125rem',
        fontWeight: 500,
        lineHeight: 1.32,
      },
      body1: {
        fontSize: '1rem',
        fontWeight: 400,
      },
      body2: {
        fontSize: '1rem',
        fontWeight: 400,
      },
      subtitle1: {
        fontSize: '0.875rem',
      },
      subtitle2: {
        fontSize: '0.75rem',
      },
      button: {
        fontWeight: 400,
      },
    },
    chatItem: chatItemColorConfig,
    sideMenuTab: sideMenuTabConfig,
    popoverSurface: popoverSurfaceConfig,
    mfaEnrollment: mfaEnrollmentConfig,
    knowledgeFile: knowledgeFileConfig,
    serviceMessagesSummary: serviceMessagesSummaryConfig,
    timelineEvent: timelineEventConfig,
    b2chatblue,
    b2chatgray,
    b2chatyellow,
    b2chatgreen,
    b2chatred,
    b2chatorange,
    capicolor,
    palette: {
      type: mode,
      background: {
        default: isDark ? DARK_BG_DEFAULT : common.white,
        paper: isDark ? DARK_BG_CARD : common.white,
      },
      primary: primaryColorConfig,
      text: isDark
        ? {
            primary: DARK_TEXT_PRIMARY,
            secondary: DARK_TEXT_SECONDARY,
            disabled: DARK_TEXT_DISABLED,
          }
        : { ...text },
      grey: b2chatgray,
      action: {
        active: alpha(primaryColorConfig.main, 0.1),
        hover: alpha(b2chatgray[700], isDark ? 0.15 : 0.05),
        selected: alpha(primaryColorConfig.main, 0.1),
      },
    },
    shadows,
    overrides: {
      MuiInputLabel: {
        root: {
          color: isDark ? DARK_TEXT_PRIMARY : text.primary,
          zIndex: 1,
          cursor: 'text',
          paddingLeft: 8,
        },
      },
      MuiFormLabel: {
        root: {
          color: isDark ? DARK_TEXT_PRIMARY : text.primary,
          marginBottom: 6,
          display: 'flex',
          flexFlow: 'row nowrap',
        },
      },
      MuiFormControlLabel: {
        root: {
          marginLeft: -3,
          '& .MuiSwitch-root ~ .MuiFormControlLabel-label': {
            marginLeft: 4,
          },
          '& .MuiSwitch-sizeSmall ~ .MuiFormControlLabel-label': {
            marginTop: 4,
            marginLeft: 4,
          },
        },
      },
      MuiFormControl: {
        root: {
          '& .MuiInputLabel-root, & .MuiInputLabel-outlined.MuiInputLabel-marginDense':
            {
              padding: '0 4px',
              top: 0,
              left: 4,
              transform: 'translate(0, 12px)',
              borderRadius: 4,
            },
          '& .MuiInputLabel-shrink[data-shrink="true"]': {
            transform: 'translate(12px, -5px) scale(0.80)',
          },
        },
        marginDense: {
          marginTop: 0,
          marginBottom: 0,
        },
      },
      MuiInputBase: {
        root: {
          '& legend': {
            marginLeft: 7,
            fontSize: '0.78rem',
          },
          '& .MuiSvgIcon-root': {
            color: isDark ? DARK_TEXT_SECONDARY : b2chatgray[500],
          },
        },
      },
      MuiOutlinedInput: {
        root: {
          border: 0,
          borderRadius: 999,
          background: isDark ? DARK_BG_INPUT : 'white',
          '&:hover': {
            border: 0,
          },
          '&$focused': {
            border: 0,
          },
          '&$multiline': {
            borderRadius: 17,
          },
        },
        focused: {},
        multiline: {},
        input: {
          paddingTop: 10,
          paddingBottom: 10,
          paddingLeft: 8,
        },
        adornedEnd: {
          paddingRight: 8,
        },
        adornedStart: {
          paddingLeft: 8,
        },
        notchedOutline: {
          borderColor: isDark ? DARK_BORDER : b2chatgray[300],
          borderWidth: '1px !important',
        },
      },
      MuiTouchRipple: {
        root: {
          opacity: 0.2,
        },
      },
      MuiPopover: {
        root: {},
        paper: {
          // Lift floating surfaces off the background — see popoverSurfaceConfig.
          border: popoverSurfaceConfig.border,
          margin: 0,
          borderRadius: 8,
          backgroundColor: popoverSurfaceConfig.background,
          boxShadow: popoverSurfaceConfig.shadow,
        },
      },
      MuiList: {
        padding: {
          paddingTop: 0,
          paddingBottom: 0,
        },
      },
      MuiListItem: {
        root: {
          color: isDark ? DARK_TEXT_SECONDARY : b2chatgray[500],
          '&$selected': {
            color: primaryColorConfig.main,
            backgroundColor: alpha(primaryColorConfig.main, 0.1),
          },
        },
        gutters: {
          paddingLeft: 12,
          paddingRight: 12,
        },
        button: {
          '&.Mui-selected': {
            color: primaryColorConfig.main,
          },
        },
      },
      MuiListItemIcon: {
        root: {
          minWidth: '0',
          marginRight: 10,
          color: 'inherit',
        },
      },
      MuiMenuItem: {
        root: {
          color: isDark ? DARK_TEXT_SECONDARY : b2chatgray[500],
          '&$selected': {
            color: primaryColorConfig.main,
            backgroundColor: alpha(primaryColorConfig.main, 0.1),
          },
        },
        gutters: {
          paddingLeft: 12,
          paddingRight: 12,
        },
      },
      MuiDialog: {
        paper: {
          borderRadius: 8,
        },
      },
      MuiDialogTitle: {
        root: {
          fontSize: 18,
          padding: '20px 30px 0px',
          '& > *': { fontSize: 'inherit' },
        },
      },
      MuiDialogContent: {
        root: {
          lineHeight: 1.4,
          padding: '20px 30px',
          '& > *': { lineHeight: 'inherit' },
        },
      },
      MuiDialogActions: {
        root: { padding: '0px 30px 20px 30px' },
      },
      MuiButton: {
        root: {
          color: b2chatgray[500],
          borderRadius: 999,
          paddingTop: 6,
          paddingBottom: 6,
          boxSizing: 'border-box',
          textTransform: 'none',
        },
        label: {
          textTransform: 'none',
          '&:first-letter': {
            textTransform: 'uppercase',
          },
        },
        containedPrimary: {
          '&:hover': {
            backgroundColor: primaryColorConfig.main,
            opacity: 0.6,
          },
          '&:active': {
            backgroundColor: primaryColorConfig.main,
            opacity: 0.85,
          },
          '&$disabled': {
            backgroundColor: isDark ? DARK_BG_INPUT : '#D4D2D5',
            color: isDark ? DARK_TEXT_DISABLED : undefined,
          },
        },
        contained: {
          backgroundColor: isDark ? DARK_BG_INPUT : b2chatgray[100],
          color: isDark ? DARK_TEXT_PRIMARY : undefined,
          ...(isDark && {
            '&:hover': {
              backgroundColor: '#2f3340',
            },
          }),
        },
        outlined: {
          paddingTop: 6,
          paddingBottom: 6,
          background: isDark ? DARK_BG_INPUT : 'white',
          borderColor: isDark ? DARK_BORDER : b2chatgray[300],
          color: isDark ? DARK_TEXT_SECONDARY : b2chatgray[500],
          '&:hover': {
            borderColor: primaryColorConfig.main,
            color: primaryColorConfig.main,
          },
          '&:active': {
            borderColor: primaryColorConfig.main,
            color: primaryColorConfig.main,
          },
          '&$disabled': {
            backgroundColor: isDark ? DARK_BG_INPUT : '#D4D2D5',
            color: isDark ? DARK_TEXT_DISABLED : undefined,
          },
        },
        outlinedPrimary: {
          color: primaryColorConfig.main,
          borderColor: primaryColorConfig.main,
          background: isDark ? DARK_BG_INPUT : 'white',
          '&:hover': {
            borderColor: primaryColorConfig.main,
            color: primaryColorConfig.main,
            opacity: 0.8,
          },
        },
        sizeSmall: {
          paddingTop: 4,
          paddingBottom: 4,
        },
      },
      MuiIconButton: {
        root: {
          color: isDark ? DARK_TEXT_PRIMARY : text.primary,
        },
        colorPrimary: {
          '&:hover': {
            backgroundColor: 'transparent',
          },
        },
      },
      MuiChip: {
        root: {
          fontWeight: 300,
          fontSize: '0.9rem',
          color: isDark ? DARK_TEXT_PRIMARY : text.primary,
          backgroundColor: isDark ? DARK_BG_INPUT : b2chatgray[200],
        },
        sizeSmall: {
          padding: '2px 4px',
        },
        deleteIcon: {
          color: isDark ? DARK_TEXT_SECONDARY : alpha(text.primary, 0.8),
          '&:hover': {
            color: isDark ? DARK_TEXT_PRIMARY : text.primary,
          },
        },
      },
      MuiTooltip: {
        tooltip: {
          boxShadow: 'unset',
          filter: 'drop-shadow(-2px 2px 10px #7070754c)',
          backgroundColor: isDark ? DARK_BG_INPUT : 'white',
          // Same hairline border as the other dark floating surfaces so the
          // tooltip separates from the background (none in light).
          border: popoverSurfaceConfig.border,
          color: isDark ? DARK_TEXT_PRIMARY : text.primary,
          padding: '8px 12px',
          borderRadius: 8,
          fontSize: '0.9rem',
          fontWeight: 400,
          maxHeight: 400,
          maxWidth: 600,
        },
        arrow: {
          color: isDark ? DARK_BG_INPUT : 'white',
        },
      },
      MuiTypography: {
        colorPrimary: {
          color: `${primaryColorConfig.main} !important`,
        },
      },
      MuiSwitch: {
        root: {
          padding: 0,
          width: 46,
          height: 26,
          margin: 4,
          borderRadius: 20,
          background: 0,
          overflow: 'visible',
          '& .MuiTouchRipple-root': {
            margin: -8,
            opacity: 0.3,
          },
        },
        track: {
          borderRadius: 20,
          backgroundColor: '#D3D3D7',
        },
        thumb: {
          width: 22,
          height: 22,
          margin: 2,
          backgroundColor: 'white',
        },
        input: {
          width: '200%',
          left: 'auto',
        },
        switchBase: {
          padding: 0,
          '&$checked': {
            '& + $thumb': {
              backgroundColor: 'white',
            },
            '& + $track': {
              opacity: 1,
              background: 'white',
            },
          },
        },
        sizeSmall: {
          paddingInline: 0,
          paddingBlock: 3,
          '& $track': {
            height: 22,
            marginLeft: 3,
            marginRight: -1,
          },
        },
      },
      MuiSlider: {
        root: {
          height: 8,
          padding: '2px 0',
        },
        rail: {
          height: 8,
          borderRadius: 4,
          backgroundColor: grey[300],
          opacity: 1,
        },
        track: {
          height: 8,
          borderRadius: 4,
          transition: 'all 0.1s',
        },
        thumb: {
          marginTop: -6,
          marginBottom: -6,
          marginLeft: -10,
          width: 20,
          height: 20,
          boxShadow: shadows.m,
          backgroundColor: primaryColorConfig.main,
          border: '5px solid white',
          boxSizing: 'border-box',
          transition: 'all 0.1s',
        },
        valueLabel: {
          top: 30,
          '& > *': {
            backgroundColor: isDark ? DARK_BG_INPUT : 'white',
            transform: 'rotate(0deg)',
            borderRadius: 4,
            boxShadow: shadows.xxs,
            width: 28,
            height: 22,
            '& > *': {
              color: isDark ? DARK_TEXT_PRIMARY : 'black',
              transform: 'rotate(0deg)',
              fontSmooth: 'always',
            },
          },
        },
      },
      MuiFormHelperText: {
        root: {
          textAlign: 'right',
        },
        contained: {
          marginLeft: 0,
          marginRight: 0,
        },
      },
      MuiLinearProgress: {
        root: {
          borderRadius: 2,
        },
        buffer: {
          borderRadius: 2,
        },
        bar: {
          borderRadius: 2,
          backgroundColor: grey[300],
        },
      },
      MuiBreadcrumbs: {
        separator: {
          color: text.primary,
        },
        li: {
          color: text.primary,
        },
      },
      MuiLink: {
        root: {
          '&:hover': {
            cursor: 'pointer',
            color: primaryColorConfig.main,
          },
        },
      },
      MuiAvatar: {
        root: {
          color: primaryColorConfig.main,
        },
      },
      ...{
        MuiAlert: {
          root: {
            borderRadius: 12,
            border: '1px solid',
            padding: '12px 16px',
            fontSize: '0.875rem',
            alignItems: 'flex-start',
          },
          standardSuccess: {
            backgroundColor: isDark
              ? 'rgba(18, 183, 106, 0.15)'
              : 'rgba(18, 183, 106, 0.08)',
            borderColor: '#12B76A',
            color: isDark ? '#6CE9A6' : '#027A48',
            '& $icon': { color: '#12B76A' },
          },
          standardWarning: {
            backgroundColor: isDark
              ? 'rgba(247, 144, 9, 0.15)'
              : 'rgba(247, 144, 9, 0.08)',
            borderColor: '#F79009',
            color: isDark ? '#FEC84B' : '#B54708',
            '& $icon': { color: '#F79009' },
          },
          standardError: {
            backgroundColor: isDark
              ? 'rgba(240, 68, 56, 0.15)'
              : 'rgba(240, 68, 56, 0.08)',
            borderColor: '#F04438',
            color: isDark ? '#FDA29B' : '#B42318',
            '& $icon': { color: '#F04438' },
          },
          standardInfo: {
            backgroundColor: isDark
              ? 'rgba(33, 150, 243, 0.15)'
              : 'rgba(33, 150, 243, 0.08)',
            borderColor: '#2196F3',
            color: isDark ? '#90CAF9' : '#0D47A1',
            '& $icon': { color: '#2196F3' },
          },
          icon: {
            alignItems: 'center',
            padding: '2px 0',
            marginRight: 12,
            opacity: 1,
          },
          message: {
            padding: '2px 0',
          },
        },
        MuiAlertTitle: {
          root: {
            fontWeight: 600,
            fontSize: '0.9rem',
            marginBottom: 2,
            lineHeight: 1.5,
          },
        },
        MuiAutocomplete: {
          paper: {
            boxShadow: shadows.xs,
            border: `1px solid ${primaryColorConfig.main}`,
            borderRadius: 8,
            margin: '4px 0',
            minWidth: 250,
          },
          listbox: {
            padding: 0,
          },
          option: {
            borderRadius: 0,
            '&[aria-selected="true"], &:active': {
              backgroundColor: 'rgb(210, 205, 255)',
              color: primaryColorConfig.main,
            },
            '&:active': {
              color: primaryColorConfig.main,
            },
          },
          groupLabel: {
            color: grey[800],
            fontWeight: 400,
            cursor: 'default',
            borderTop: `1px solid ${grey[200]}`,
            padding: '0px 8px',
            lineHeight: '2.8rem',
            textAlign: 'bottom',
          },
        },
      },
      MuiCheckbox: {
        colorPrimary: {
          '&$indeterminate': {
            color: primaryColorConfig.main,
          },
        },
        indeterminate: {},
      },
      MuiPaper: {
        outlined: {
          border: `1px solid ${b2chatgray[400]}`,
        },
      },
      MuiTabs: {
        root: {
          minHeight: 32,
          overflow: 'visible',
        },
        indicator: {
          height: 3,
          backgroundColor: primaryColorConfig.main,
          borderRadius: 3,
        },
        //@ts-ignore
        vertical: {
          '& .MuiTab-root': {
            flexGrow: 0,
            flexBasis: 'auto',
            minWidth: 'auto',
          },
        },
      },
      MuiTab: {
        root: {
          flexGrow: 1,
          flexBasis: 100,
          minWidth: 'auto !important',
          minHeight: 32,
          overflow: 'visible',

          textTransform: 'none',
          borderTopRightRadius: 8,
          borderTopLeftRadius: 8,
          backgroundColor: 'transparent',
          transition: transitions.create('background-color', {
            duration: transitions.duration.standard,
            easing: transitions.easing.easeIn,
          }),
          fontWeight: 400,
          '&$selected': {
            fontWeight: 600,
            backgroundColor: 'rgba(124, 105, 255, 0.1)',
            color: primaryColorConfig.main,
          },
        },
      },
    },

    props: {
      MuiButtonBase: {},
      MuiButton: {
        disableElevation: true,
      },
      MuiChip: {
        deleteIcon: <CloseRoundedIcon />,
      },
      MuiInputBase: {
        margin: 'dense',
      },
      MuiInputLabel: {
        margin: 'dense',
      },
      MuiInput: {
        margin: 'dense',
      },
      MuiOutlinedInput: {
        margin: 'dense',
      },
      MuiTextField: {
        margin: 'dense',
        variant: 'outlined',
        InputProps: {
          margin: 'dense',
        },
        InputLabelProps: {
          margin: 'dense',
        },
      },
      MuiSelect: {
        variant: 'outlined',
        margin: 'none',
        IconComponent: ArrowDropDownRounded,
        MenuProps: {
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
          marginThreshold: 0,
          getContentAnchorEl: null,
          PaperProps: {
            style: {
              marginTop: 4, // Adds spacing between input and menu
            },
          },
        },
      },
      MuiSwitch: {
        color: 'primary',
      },
      MuiRadio: {
        color: 'primary',
      },
      MuiTooltip: {
        arrow: true,
      },
      MuiCheckbox: {
        color: 'primary',
        checkedIcon: (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            bgcolor="background.paper"
            boxShadow="0 0 0 1px currentColor"
            borderRadius={4}
            width={18}
            height={18}
          >
            <svg
              width="10"
              height="8"
              viewBox="0 0 10 8"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M3.39644 7.5821L0.14644 4.3321C-0.0488135 4.13684 -0.0488135 3.82026 0.14644 3.62499L0.85353 2.91788C1.04878 2.72261 1.36539 2.72261 1.56064 2.91788L3.74999 5.10721L8.43935 0.417879C8.6346 0.222625 8.95121 0.222625 9.14646 0.417879L9.85355 1.12499C10.0488 1.32024 10.0488 1.63682 9.85355 1.8321L4.10355 7.58212C3.90828 7.77737 3.59169 7.77737 3.39644 7.5821Z"
                fill="currentColor"
              />
            </svg>
          </Box>
        ),
        icon: (
          <Box
            bgcolor="background.paper"
            boxShadow={`0 0 0 1px ${grey[400]}`}
            borderRadius={4}
            width={18}
            height={18}
          />
        ),
        indeterminateIcon: (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            bgcolor="background.paper"
            color="currentColor"
            boxShadow="0 0 0 1px currentColor"
            borderRadius={4}
            width={18}
            height={18}
          >
            <svg
              width="10"
              height="2"
              viewBox="0 0 10 2"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect width="10" height="2" rx="1" fill="currentColor" />
            </svg>
          </Box>
        ),
      },
    },
  });

  return (
    <MuiV4ThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </MuiV4ThemeProvider>
  );
};

export default customMuiTheme;
